import { ChevronDown, ChevronRight, ListTodo, Plus } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  groupTasksForPanel,
  type Task,
  type TaskStatusMeta,
  type WorkspaceMember,
} from '../../lib/task-list';
import { trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { Button } from '../../ui/button';
import { CreateTaskDialog } from './CreateTaskDialog';
import { TaskCard } from './TaskCard';

/**
 * What a task created from this panel is attached to.
 *
 * The panel opens the shared dialog itself rather than exposing a free-form `onCreate`. Six
 * callers each inventing an answer to "what does Add Task do" is what produced two dead ends
 * that navigated to an unfiltered task index and created nothing; a declared context cannot
 * produce a seventh.
 */
export type TaskPanelCreateContext =
  | { contactId: number }
  | { anchorId: number }
  | { threadId: string };

export interface TaskPanelProps {
  tasks: Task[];
  statuses: TaskStatusMeta[];
  members: WorkspaceMember[];
  isLoading?: boolean;
  isReadOnly?: boolean;
  onTaskClick: (taskId: number) => void;
  onToggleComplete?: (task: Task) => void;
  /** Omit to hide the create affordance entirely (a read-only or fixed list). */
  createContext?: TaskPanelCreateContext;
  /** Header button by default; `floating` is the extension side panel's FAB. */
  createAffordance?: 'header' | 'floating';
  /**
   * How creating is *presented*. The panel still decides what creating means and pre-links the
   * entity either way — this only picks modal vs full page. `route` exists because the browser
   * extension's side panel is ~400px wide, where a ten-field modal does not fit; it navigates to
   * `createRoute`, carrying the create context in router state.
   */
  createMode?: 'dialog' | 'route';
  /** Required when `createMode` is `route` — where the full-page authoring form lives. */
  createRoute?: string;
  /**
   * Entity-derived starting values for a new task — a thread pre-fills the title from its subject
   * and the description from its summary. Only the host can compute these; the panel still owns
   * everything else about creating.
   */
  createDefaults?: { title?: string; description?: string };
  /** Host-specific cache invalidation after a create — the panel invalidates the task queries. */
  onCreated?: () => void;
  /**
   * Optionally control the create dialog from outside, for a host whose own "Add Task" button
   * lives above the panel (a detail page header shown on every tab, say). This is still not a
   * free-form create callback — the panel decides what creating *does*; the host only decides
   * when the dialog opens.
   */
  createOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
  /** Section heading. Omit for surfaces that already have one above the panel. */
  title?: string;
  /** A trailing per-task control — an overflow menu, a schedule button. */
  renderTaskAction?: (task: Task) => ReactNode;
  /** An extra chip in a card's meta row — the extension's repeat schedule. */
  renderTaskMeta?: (task: Task) => ReactNode;
  /** Replaces the default "nothing here yet" block. */
  emptyState?: ReactNode;
  /** Lines of description preview on each card. */
  descriptionLines?: 0 | 1 | 2;
  className?: string;
}

function toCreateFields(context: TaskPanelCreateContext) {
  if ('contactId' in context) return { contactId: context.contactId };
  if ('anchorId' in context) return { anchorId: context.anchorId };
  return { anchorThreadId: context.threadId };
}

/**
 * The narrow-column task list — the panel shown beside an entity.
 *
 * One implementation for every such surface: the contact detail rail, a thread's linked tasks,
 * the extension's side panel, and the calendar's unscheduled list. It owns the grouped sections,
 * the collapsible completed section, the loading and empty states, and creating a task.
 *
 * Cards come from `TaskCard`; bucketing from `groupTasksForPanel`. This is a *container*, a
 * different axis from the three card/row/table form factors in ADR-0009 — not a fourth one.
 */
export function TaskPanel({
  tasks,
  statuses,
  members,
  isLoading = false,
  isReadOnly = false,
  onTaskClick,
  onToggleComplete,
  createContext,
  createAffordance = 'header',
  createMode = 'dialog',
  createRoute,
  createDefaults,
  onCreated,
  createOpen,
  onCreateOpenChange,
  title,
  renderTaskAction,
  renderTaskMeta,
  emptyState,
  descriptionLines = 0,
  className,
}: TaskPanelProps) {
  const [uncontrolledCreateOpen, setUncontrolledCreateOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  const navigate = useNavigate();

  const isCreateOpen = createOpen ?? uncontrolledCreateOpen;
  const setIsCreateOpen = (open: boolean) => {
    setUncontrolledCreateOpen(open);
    onCreateOpenChange?.(open);
  };

  /** One entry point, so neither affordance can drift from the other. */
  const startCreate = () => {
    if (createMode === 'route' && createRoute) {
      navigate(createRoute, { state: createContext ? toCreateFields(createContext) : undefined });
      return;
    }
    setIsCreateOpen(true);
  };

  const utils = trpc.useUtils();
  const createMutation = trpc.tasks.create.useMutation({
    onSuccess: () => {
      utils.tasks.invalidate();
      toast.success('Task created');
      setIsCreateOpen(false);
      onCreated?.();
    },
    onError: (error) => toast.error(`Failed to create task: ${error.message}`),
  });

  const groups = groupTasksForPanel(tasks, statuses, 'panel');
  const openGroups = groups.filter((group) => group.key !== 'completed');
  const completedGroup = groups.find((group) => group.key === 'completed');
  // In route mode the destination page can resolve its own context (the extension's create page
  // derives the anchor from the active tab), so an explicit context is optional there. In dialog
  // mode it is required — the dialog has nothing else to link against.
  const canCreate = !isReadOnly && (!!createContext || (createMode === 'route' && !!createRoute));

  const card = (task: Task) => (
    <TaskCard
      key={task.id}
      task={task}
      statuses={statuses}
      members={members}
      onClick={() => onTaskClick(task.id)}
      onToggleComplete={isReadOnly ? undefined : onToggleComplete}
      descriptionLines={descriptionLines}
      metaSlot={renderTaskMeta?.(task)}
      actionSlot={isReadOnly ? undefined : renderTaskAction?.(task)}
      className="group"
    />
  );

  return (
    <div className={cn('relative flex flex-col', className)}>
      {(title || (canCreate && createAffordance === 'header')) && (
        <div className="flex shrink-0 items-center justify-between pb-3">
          {title && (
            <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <ListTodo className="h-3.5 w-3.5 text-primary" />
              {title}{' '}
              {tasks.length > 0 && `(${openGroups.reduce((n, g) => n + g.tasks.length, 0)})`}
            </h3>
          )}
          {canCreate && createAffordance === 'header' && (
            <Button size="sm" onClick={startCreate} className="h-7 gap-1 px-3 text-xs">
              <Plus className="h-3.5 w-3.5" /> Add Task
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array(3)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-muted" />
            ))}
        </div>
      ) : tasks.length === 0 ? (
        (emptyState ?? (
          <div className="rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center">
            <p className="text-xs italic text-muted-foreground">No tasks yet.</p>
            {canCreate && (
              <Button
                variant="outline"
                size="sm"
                onClick={startCreate}
                className="mt-3 h-7 gap-1 text-xs"
              >
                <Plus className="h-3.5 w-3.5" /> Add the first one
              </Button>
            )}
          </div>
        ))
      ) : (
        <div className="space-y-4">
          {openGroups.map((group) => (
            <div key={group.key} className="space-y-2">
              <h4
                className={cn(
                  'text-xs font-semibold uppercase tracking-wider text-muted-foreground',
                  group.colorClass
                )}
              >
                {group.title} ({group.tasks.length})
              </h4>
              <div className="space-y-2">{group.tasks.map(card)}</div>
            </div>
          ))}

          {completedGroup && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowCompleted(!showCompleted)}
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
              >
                {showCompleted ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                Completed ({completedGroup.tasks.length})
              </button>
              {showCompleted && <div className="space-y-2">{completedGroup.tasks.map(card)}</div>}
            </div>
          )}
        </div>
      )}

      {canCreate && createAffordance === 'floating' && (
        <div className="absolute bottom-6 right-6">
          <Button onClick={startCreate} size="icon" className="size-12 rounded-full shadow-lg">
            <Plus className="size-6" />
            <span className="sr-only">Add task</span>
          </Button>
        </div>
      )}

      {createContext && createMode === 'dialog' && (
        <CreateTaskDialog
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate({ ...data, ...toCreateFields(createContext) })}
          initialTitle={createDefaults?.title ?? ''}
          initialDescription={createDefaults?.description ?? ''}
          initialContactId={'contactId' in createContext ? createContext.contactId : null}
          initialAnchorId={'anchorId' in createContext ? createContext.anchorId : null}
          workspaceMembers={members}
          isSubmitting={createMutation.isPending}
        />
      )}
    </div>
  );
}
